import type { FC } from "react";
import { useEffect, useState } from "react";

import { Link, useLoaderData, useLocation } from "@remix-run/react";
import WvLoginCreateAccount from "~/components/organisms/WvLoginCreateAccount";
import WvLoginForm from "~/components/organisms/WvLoginForm";
import { asText } from "@prismicio/helpers";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

import { metaHelper } from "~/lib/meta";

import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import {
  initAction,
  loginAction,
  logoutAction,
  requestActivationAction,
} from "@commercekit/remix/lib.server";
import { getSeoFromDocument } from "@commercekit/prismic";
import { cacheHeaders } from "@commercekit/shared";
import { AuthActions } from "@commercekit/remix";
import { initAppLoader } from "lib/initLoader.server";
export const meta = metaHelper;
export { default as ErrorBoundary } from "~/components/atoms/GeneralErrorBoundary";

interface EmailState {
  email: string;
  resetSuccess: string;
}

const LoginPage: FC = () => {
  const { content, businuessCusotmerAdvantagesSlice } =
    useLoaderData<typeof loader>();

  const [tokenExpired, setTokenExpired] = useState(false);
  const [emailResetMessage, setEmailResetMessage] = useState("");
  const [passwordResetMessage, setPasswordResetMessage] = useState("");
  const { $pt } = usePrismicLabels();
  const location = useLocation();
  const accountResetPasswordEmailSent = `${$pt(
    "account_reset_password_email_sent"
  )}`;
  const expiredTokenMessage = $pt("token_expired_message");

  useEffect(() => {
    const state = location.state as EmailState;
    const _email_message = state?.email
      ? accountResetPasswordEmailSent?.replace("{EMAIL}", state?.email)
      : ``;

    const password_message = state?.resetSuccess ?? "";
    setEmailResetMessage(_email_message);
    setPasswordResetMessage(password_message);
    setTokenExpired(
      new URLSearchParams(location.search).get("token") === "expired"
    );
    return window.history.replaceState({}, document.title);
  }, [location]);

  return (
    <div className="customer">
      <div className="container">
        <div className="py-3 mb-3 breadcrumbs"></div>

        <div className="row">
          <div className="col-12 col-md-7 col-lg-6">
            {tokenExpired && expiredTokenMessage && (
              <div className="alert">
                {expiredTokenMessage.split("{")?.[0]}
                <Link
                  to={
                    new URLSearchParams(location.search).get("redirect") ?? "/"
                  }
                  style={{
                    color: "#e7ae23",
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  {expiredTokenMessage.split("{")?.[1]?.split("}")?.[0]}
                </Link>
                {expiredTokenMessage.split("{")?.[1]?.split("}")?.[1]}
              </div>
            )}

            {emailResetMessage && (
              <div className="alert">{emailResetMessage}</div>
            )}

            {passwordResetMessage ? (
              <div className="alert alert-success">{passwordResetMessage}</div>
            ) : null}

            <h1 className="pb-3 mb-3 heading sans-serif mdl strong underlined left">
              {asText(content.title)}
            </h1>
            <WvLoginForm
              inActiveAccountMessage={asText(
                content.inactive_account_error_message
              )}
            />
          </div>
          <div className="col-12 col-md-5 col-lg-6">
            <WvLoginCreateAccount
              businuessCusotmerAdvantagesSlice={
                businuessCusotmerAdvantagesSlice
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

export const action = async (args: ActionFunctionArgs) => {
  const helper = await initAction<AuthActions>(args);
  switch (helper.action) {
    case AuthActions.LOGIN: {
      const [errors, res] = await loginAction(helper);
      if (errors) return helper.error(errors);
      const adminRedirect = helper.formData.get("adminRedirect");
      if (
        adminRedirect &&
        ["ADMIN", "MANAGER", "SALES_REPRESENTATIVE"].includes(res?.role || "")
      ) {
        return helper.redirect(adminRedirect, { updateSession: true });
      }
      break;
    }

    case AuthActions.REQUEST_ACTIVATION: {
      const [errors, isSent] = await requestActivationAction(helper);
      if (errors) return helper.error(errors);
      return [null, isSent];
    }

    case AuthActions.LOGOUT: {
      const [errors] = await logoutAction(helper);
      if (errors) return helper.error(errors);
      break;
    }
  }
  return helper.response({ updateSession: true });
};

export const loader = async (args: LoaderFunctionArgs) => {
  const helper = await initAppLoader(args);

  const login = await helper.cms.getLoginPage();

  const { title, description, slices, inactive_account_error_message } =
    login.data;

  const businuessCusotmerAdvantagesSlice = slices?.filter((slice) => {
    return slice.slice_type === "businuess_cusotmer_advantages";
  });

  const seo = getSeoFromDocument(login);
  return json(
    {
      content: { title, description, inactive_account_error_message },
      seo,
      businuessCusotmerAdvantagesSlice,
    },
    { headers: cacheHeaders() }
  );
};
